import { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { useFleetSensors } from 'Views/SensorOverview/useFleetSensors';
import { sensorTypeLabel } from 'Views/SensorOverview/model';
import type { AlertRule, SensorScope } from './contracts';
import { useAlertActions, useUnifiedRules, unifiedAlertsService } from './useUnifiedAlerts';
import type { UnifiedAlertsService } from './useUnifiedAlerts';
import { alertDate, ErrorNotice, LoadingNotice } from './shared';
import { ruleModeLabels } from './ruleHelpers';
import RuleExplanation from './RuleExplanation';
import RuleEditor from './RuleEditor';
import { useAlertMigration } from './migrationTransport';

export default function RuleManager({
  scope,
  service = unifiedAlertsService,
}: {
  scope?: SensorScope;
  service?: UnifiedAlertsService;
}) {
  const { query, rules, capabilities } = useUnifiedRules(service);
  const hasScope = Boolean(scope);
  const rulePageCount = query.data?.pages.length || 0;
  useEffect(() => {
    if (hasScope && query.hasNextPage && !query.isFetching && !query.isError) {
      void query.fetchNextPage({ cancelRefetch: false });
    }
  }, [
    hasScope,
    rulePageCount,
    query.hasNextPage,
    query.isFetching,
    query.isError,
    query.fetchNextPage,
  ]);
  const inventory = useFleetSensors();
  const migrationQuery = useAlertMigration(scope);
  const activeMigration =
    !migrationQuery.isError && migrationQuery.data?.migration?.status === 'active'
      ? migrationQuery.data.migration
      : null;
  const { saveRule } = useAlertActions(service);
  const [editing, setEditing] = useState<{ rule?: AlertRule } | null>(null);
  const sensors = inventory.data || [];
  const sensor = scope
    ? sensors.find((item) => item.orgId === scope.orgId && item.sensorId === scope.sensorId)
    : undefined;
  const scopedUnavailable = Boolean(scope && !sensor);
  const shownRules = !scope
    ? rules
    : !sensor
    ? []
    : rules.filter(
        (rule) =>
          rule.scope.sensorTypes.includes(sensor.type) &&
          (!activeMigration ||
            !rule.isDefault ||
            activeMigration.ruleMappings.some((mapping) => mapping.ruleId === rule.id)) &&
          (!rule.scope.orgId || rule.scope.orgId === scope.orgId) &&
          (!rule.scope.sensorId || rule.scope.sensorId === scope.sensorId)
      );
  const currentRule = editing?.rule
    ? rules.find((rule) => rule.id === editing.rule?.id) || editing.rule
    : undefined;
  return (
    <section className="ua-stack" aria-label="Alarmregler">
      <div className="ua-header">
        <div>
          <h2>{scope ? 'Alarmregler for enheden' : 'Alarmregler på tværs af enheder'}</h2>
          <p className="ua-muted">
            {scope
              ? `Her ændrer du alarmgrænser og beskedkanaler${
                  sensor ? ` for ${sensor.sensorName}` : ''
                }. Åbn den alarmregel, du vil ændre.`
              : 'Fælles regler kan omfatte flere kunder og enheder. Brug enhedens Indstillinger til den daglige opsætning.'}
          </p>
        </div>
        <Button
          type="button"
          disabled={
            query.isPending ||
            query.isError ||
            inventory.isPending ||
            inventory.isError ||
            scopedUnavailable
          }
          onClick={() => setEditing({})}
        >
          {scope ? 'Tilføj ekstra alarmregel' : 'Opret regel'}
        </Button>
      </div>
      <p className="ua-notice">
        {activeMigration
          ? 'Den samlede alarmstyring er aktiv for enheden. Redigér en eksisterende regel for at ændre alarmen. En ekstra regel kan give flere alarmer for samme måling.'
          : 'At gemme en regel overtager ikke de eksisterende beskeder. Den samlede alarmstyring skal først aktiveres under Indstillinger for den enkelte enhed. Nye regler kan tilføje flere alarmer.'}
      </p>
      {query.isPending && <LoadingNotice>Henter regler…</LoadingNotice>}
      {scope && query.hasNextPage && !query.isError && (
        <LoadingNotice>Henter resten af enhedens regler…</LoadingNotice>
      )}
      {scope && migrationQuery.isError && (
        <p className="ua-notice ua-warning" role="alert">
          Enhedens aktive alarmopsætning kunne ikke bekræftes. De indlæste regler kan derfor også
          omfatte standardregler, som er erstattet af en overført opsætning.
        </p>
      )}
      <ErrorNotice
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
      />
      <ErrorNotice
        error={
          inventory.isError
            ? new Error(
                'Enhedslisten kunne ikke hentes. Enheder og organisationsvalg er utilgængelige.'
              )
            : null
        }
        onRetry={() => void inventory.refetch()}
      />
      {scope && inventory.isPending && <LoadingNotice>Henter enhedens oplysninger…</LoadingNotice>}
      {scopedUnavailable && !inventory.isPending && !inventory.isError && (
        <p role="alert" className="ua-notice ua-warning">
          Enheden findes ikke i den indlæste oversigt. Dens gældende regler kan derfor ikke
          fastslås.
        </p>
      )}
      <div className="ua-rule-list">
        {shownRules.map((rule) => {
          const org = sensors.find((item) => item.orgId === rule.scope.orgId)?.orgName;
          const target = sensors.find(
            (item) => item.orgId === rule.scope.orgId && item.sensorId === rule.scope.sensorId
          );
          const sharedRule = Boolean(scope && !rule.scope.sensorId);
          return (
            <article className="ua-card ua-rule-card" key={rule.id}>
              <div className="ua-header">
                <h3>{rule.title}</h3>
                <span className="ua-pill">
                  {rule.enabled ? ruleModeLabels[rule.mode] : 'Slået fra'}
                </span>
              </div>
              <div className="ua-capabilities">
                {rule.scope.sensorTypes.map((type) => (
                  <span className="ua-pill" key={type}>
                    {sensorTypeLabel(type)}
                  </span>
                ))}
                {rule.isDefault && <span className="ua-pill">Standardregel</span>}
                {rule.id.startsWith('legacy_') && (
                  <span className="ua-pill">Overført opsætning</span>
                )}
              </div>
              <p className="ua-muted">
                {rule.scope.sensorId
                  ? target?.sensorName || `Enhed ${rule.scope.sensorId}`
                  : rule.scope.orgId
                  ? org || `Organisation ${rule.scope.orgId}`
                  : 'Alle matchende enheder'}
              </p>
              {sharedRule && (
                <p className="ua-notice ua-warning">
                  Fælles regel: ændringer gælder alle matchende enheder
                  {rule.scope.orgId ? ` hos ${org || rule.scope.orgId}` : ' på tværs af kunder'}.
                </p>
              )}
              <RuleExplanation
                rule={rule}
                imported={rule.id.startsWith('legacy_')}
                recipients={
                  activeMigration?.ruleMappings.find((mapping) => mapping.ruleId === rule.id)
                    ?.recipients
                }
              />
              {rule.description && <p className="ua-muted">{rule.description}</p>}
              <span className="ua-muted">
                {rule.severity === 'critical' ? 'Kritisk' : 'Advarsel'}
              </span>
              <small className="ua-muted">
                Version {rule.version} · {alertDate(rule.updatedAt)}
              </small>
              <div className="ua-actions">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={inventory.isPending || inventory.isError || query.isError}
                  onClick={() => setEditing({ rule })}
                >
                  Rediger regel
                </Button>
              </div>
            </article>
          );
        })}
      </div>
      {!query.isPending &&
        !query.isError &&
        !scopedUnavailable &&
        !(scope && query.hasNextPage) &&
        shownRules.length === 0 && (
          <p className="ua-empty">
            Ingen matchende regler er indlæst
            {query.hasNextPage ? '. Hent flere regler for at fortsætte.' : '.'}
          </p>
        )}
      {query.hasNextPage && !scope && (
        <Button
          type="button"
          variant="outline"
          disabled={query.isFetching}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? 'Henter flere…' : 'Hent flere regler'}
        </Button>
      )}
      <div>
        <Button
          type="button"
          variant="outline"
          disabled={query.isFetching || inventory.isFetching}
          onClick={() => {
            void query.refetch();
            void inventory.refetch();
          }}
        >
          Opdatér regler og enheder
        </Button>
      </div>
      {editing && (
        <RuleEditor
          key={editing.rule?.id || 'new'}
          rule={currentRule}
          capabilities={capabilities}
          sensors={sensors}
          scope={editing.rule ? undefined : scope}
          sensorType={sensor?.type}
          onSave={saveRule}
          onPreview={service.previewRule}
          onClose={() => setEditing(null)}
          onReload={async () => {
            const result = await query.refetch();
            return result.data?.pages
              .flatMap((page) => page.rules)
              .find((item) => item.id === editing.rule?.id);
          }}
        />
      )}
    </section>
  );
}
